import { Fragment, useEffect, useState } from "react";
import { api, formatErr } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select";
import { toast } from "sonner";
import { SectionCard, EmptyState, StatusPill, ReasonDialog, useAccountingPermissions } from "./shared";

function formatMoney(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * PHASE 18 - FINANCIAL YEAR CLOSING.
 *
 * Reuses the existing Financial Year list/status, the existing Trial
 * Balance/Balance Sheet/Income & Expenditure/Member Receivables/Vendor
 * Payables report endpoints for the pre-close check, and the existing
 * Financial Year "locked" status for the actual block on new activity - see
 * backend PHASE 18 section for the full design notes. This page is the one
 * guided workflow for all of it: pre-close verification, Close (with the
 * Surplus/Deficit transfer), and Reopen (with a required reason).
 */
export default function FinancialYearClosingPage() {
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [precheck, setPrecheck] = useState(null);
  const [precheckLoading, setPrecheckLoading] = useState(false);
  const [fundLedgers, setFundLedgers] = useState([]);
  const [surplusLedgerId, setSurplusLedgerId] = useState("");
  const [busy, setBusy] = useState(false);
  const [reopenTarget, setReopenTarget] = useState(null);
  // PHASE 19: this page is already hidden from the Administration nav menu
  // for anyone lacking accounting.fyclose (see accountingNav.js /
  // AccountingShell.jsx), but a direct URL visit would otherwise still show
  // working Close/Reopen buttons - gate them here too as defense in depth,
  // matching what the backend's fy_closing_close / fy_closing_reopen
  // endpoints already require.
  const perms = useAccountingPermissions();

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get("/society/accounting/fy-closing");
      setYears(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(formatErr(err, "Could not load Financial Year Closing"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/society/accounting/engine/ledgers");
        setFundLedgers((Array.isArray(res.data) ? res.data : []).filter((l) => l.ledgerGroup === "fund" || l.ledgerGroup === "equity"));
      } catch { setFundLedgers([]); }
    })();
  }, []);

  const expand = async (fy) => {
    if (expandedId === fy.id) { setExpandedId(null); setPrecheck(null); return; }
    setExpandedId(fy.id);
    setPrecheck(null);
    setSurplusLedgerId(fy.surplusLedgerId || "");
    try {
      setPrecheckLoading(true);
      const res = await api.get(`/society/accounting/fy-closing/${fy.id}/precheck`);
      setPrecheck(res.data);
    } catch (err) {
      toast.error(formatErr(err, "Could not run pre-close check"));
    } finally {
      setPrecheckLoading(false);
    }
  };

  const closeYear = async (fy) => {
    if (!surplusLedgerId) { toast.error("Select the Accumulated Surplus / General Fund ledger first"); return; }
    if (!window.confirm(`Close Financial Year ${fy.code}? This posts the year-end closing entry, transfers Surplus/Deficit to the chosen Fund ledger, and blocks new Create/Edit/Delete for transactions dated in this year.`)) return;
    try {
      setBusy(true);
      const res = await api.post(`/society/accounting/fy-closing/${fy.id}/close`, { surplusLedgerId });
      toast.success(`Financial Year ${fy.code} closed. ${res.data.surplus ? `Surplus ${formatMoney(res.data.surplus)}` : res.data.deficit ? `Deficit ${formatMoney(res.data.deficit)}` : "No Surplus/Deficit"} transferred.`);
      setExpandedId(null);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.errors) {
        toast.error(`Cannot close: ${detail.errors.join(" ")}`);
      } else {
        toast.error(formatErr(err, "Could not close Financial Year"));
      }
    } finally {
      setBusy(false);
    }
  };

  const confirmReopen = async (reason) => {
    if (!reopenTarget) return;
    try {
      setBusy(true);
      await api.post(`/society/accounting/fy-closing/${reopenTarget.id}/reopen`, { reason });
      toast.success(`Financial Year ${reopenTarget.code} reopened`);
      setReopenTarget(null);
      setExpandedId(null);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not reopen Financial Year"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Financial Year Closing</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Pre-close verification, year-end closing (Surplus/Deficit transfer), and Reopen for corrections - Bank/Cash/Member/Vendor/Fund balances all carry forward automatically, with no re-entry.</p>
      </div>

      <SectionCard>
        {loading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : years.length === 0 ? (
          <EmptyState>No Financial Years yet. Add one under Financial Years first.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E7EB] text-left text-xs uppercase tracking-[0.12em] text-[#6B7280]">
                  <th className="py-2 pr-3">Financial Year</th>
                  <th className="py-2 pr-3">Start Date</th>
                  <th className="py-2 pr-3">End Date</th>
                  <th className="py-2 pr-3">Accounting Status</th>
                  <th className="py-2 pr-3">Trial Balance</th>
                  <th className="py-2 pr-3">Closing Status</th>
                  <th className="py-2 pr-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {years.map((fy) => (
                  <Fragment key={fy.id}>
                    <tr className="border-b border-[#F3F4F6]">
                      <td className="py-2 pr-3 font-semibold text-[#111827]">{fy.code}{fy.isCurrent && <span className="ml-2 rounded-sm bg-[#111827] px-2 py-0.5 text-[10px] font-semibold text-white">Current</span>}</td>
                      <td className="py-2 pr-3">{fy.startDate}</td>
                      <td className="py-2 pr-3">{fy.endDate}</td>
                      <td className="py-2 pr-3">{fy.accountingStatus === "Locked" ? <StatusPill active={false} falseLabel="Locked" /> : fy.accountingStatus === "Closing" ? <span className="rounded-sm bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">Closing</span> : <StatusPill active trueLabel="Open" />}</td>
                      <td className="py-2 pr-3">{fy.trialBalanceStatus === "Balanced" ? <StatusPill active trueLabel="Balanced" /> : <StatusPill active={false} falseLabel="Not Balanced" />}</td>
                      <td className="py-2 pr-3">{fy.closingStatus === "Closed" ? <StatusPill active={false} falseLabel="Closed" /> : <StatusPill active trueLabel="Not Closed" />}</td>
                      <td className="py-2 pr-3 text-right">
                        {!perms.canCloseFY ? (
                          <span className="text-xs text-[#9CA3AF]">No permission</span>
                        ) : fy.status === "locked" ? (
                          <Button size="sm" variant="outline" onClick={() => setReopenTarget(fy)}>Reopen Financial Year</Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => expand(fy)}>{expandedId === fy.id ? "Hide" : "Review & Close"}</Button>
                        )}
                      </td>
                    </tr>
                    {expandedId === fy.id && (
                      <tr key={`${fy.id}-detail`}>
                        <td colSpan={7} className="bg-[#F9FAFB] p-4">
                          {precheckLoading ? (
                            <div className="text-sm text-[#6B7280]">Running pre-close check...</div>
                          ) : precheck ? (
                            <div className="space-y-3">
                              <div className="grid gap-3 sm:grid-cols-3">
                                <div className="rounded-sm border border-[#E5E7EB] bg-white p-3">
                                  <div className="text-[11px] uppercase tracking-[0.12em] text-[#6B7280]">Trial Balance</div>
                                  <div className={`mt-1 text-sm font-bold ${precheck.trialBalance.balanced ? "text-emerald-700" : "text-red-700"}`}>{precheck.trialBalance.balanced ? "Balanced" : "Not Balanced"}</div>
                                </div>
                                <div className="rounded-sm border border-[#E5E7EB] bg-white p-3">
                                  <div className="text-[11px] uppercase tracking-[0.12em] text-[#6B7280]">Balance Sheet</div>
                                  <div className={`mt-1 text-sm font-bold ${precheck.balanceSheet.balanced ? "text-emerald-700" : "text-red-700"}`}>{precheck.balanceSheet.balanced ? "Balanced" : `Mismatch ${formatMoney(precheck.balanceSheet.difference)}`}</div>
                                </div>
                                <div className="rounded-sm border border-[#E5E7EB] bg-white p-3">
                                  <div className="text-[11px] uppercase tracking-[0.12em] text-[#6B7280]">{precheck.incomeExpenditure.isDeficit ? "Deficit" : "Surplus"} for the year</div>
                                  <div className="mt-1 text-sm font-bold text-[#111827]">{formatMoney(precheck.incomeExpenditure.isDeficit ? precheck.incomeExpenditure.deficit : precheck.incomeExpenditure.surplus)}</div>
                                </div>
                              </div>

                              {precheck.errors.length > 0 && (
                                <div className="rounded-sm border border-red-300 bg-red-50 p-3 text-sm text-red-700">
                                  <div className="font-bold">Cannot close - fix these first:</div>
                                  <ul className="mt-1 list-disc pl-5">{precheck.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                                </div>
                              )}
                              {precheck.warnings.length > 0 && (
                                <div className="rounded-sm border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                                  <div className="font-bold">Warnings (will not block closing):</div>
                                  <ul className="mt-1 list-disc pl-5">{precheck.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                                </div>
                              )}

                              {precheck.canClose && (
                                <div className="flex flex-wrap items-end gap-3 rounded-sm border border-[#E5E7EB] bg-white p-3">
                                  <div className="w-72 space-y-1">
                                    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Transfer Surplus/Deficit to Fund Ledger</div>
                                    <Select value={surplusLedgerId} onValueChange={setSurplusLedgerId}>
                                      <SelectTrigger><SelectValue placeholder="Select Accumulated Surplus / Fund ledger" /></SelectTrigger>
                                      <SelectContent>
                                        {fundLedgers.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                                      </SelectContent>
                                    </Select>
                                    {fundLedgers.length === 0 && <p className="text-[11px] text-amber-700">No Fund/Reserve ledger exists yet - create one under Masters &gt; Funds first.</p>}
                                  </div>
                                  <Button disabled={busy || !surplusLedgerId} onClick={() => closeYear(fy)} className="bg-[#111827] text-white hover:bg-[#374151]">CLOSE FINANCIAL YEAR</Button>
                                </div>
                              )}
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <ReasonDialog
        open={!!reopenTarget}
        onOpenChange={(open) => { if (!open) setReopenTarget(null); }}
        title={`Reopen Financial Year ${reopenTarget?.code || ""}?`}
        description="This removes the year-end closing entry (the Surplus/Deficit transfer) and unlocks the year for Create/Edit/Delete again. A later, already-closed Financial Year will block this if it depends on this year's closing figures. A reason is required and is recorded permanently."
        busy={busy}
        confirmLabel="Reopen Financial Year"
        onConfirm={confirmReopen}
      />
    </div>
  );
}
